// Copyright (c) Bentley Systems
import { useEffect, useRef, useState } from "react";
import {
  BaseFilter,
  Button,
  Input,
} from "@itwin/itwinui-react";
import type { TableFilterProps } from "@itwin/itwinui-react/react-table";

const MIN_FILTER_LENGTH = 3;
const HINT_ID = "odata-filter-hint";

/**
 * A drop-in replacement for `tableFilters.TextFilter()` that enforces a
 * minimum input length before the OData filter is applied.
 *
 * - Values shorter than {@link MIN_FILTER_LENGTH} characters show a
 *   validation hint and the "Filter" button is disabled.
 * - Clearing the input always works (resets the filter).
 *
 * Uses the same iTwinUI primitives (`BaseFilter`, `Input`, `Button`)
 * so the look-and-feel matches the built-in `TextFilter`.
 */
export const ODataTextFilter = <T extends Record<string, unknown>>(
  props: TableFilterProps<T>,
) => {
  const { column, setFilter, clearFilter } = props;
  const [text, setText] = useState<string>(
    (column.filterValue as string) ?? "",
  );
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const isValid = text.length >= MIN_FILTER_LENGTH;
  const showHint = text.length > 0 && !isValid;

  return (
    <BaseFilter
      onSubmit={() => {
        if (isValid) {
          setFilter(text);
        }
      }}
    >
      <Input
        ref={inputRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={`Min ${MIN_FILTER_LENGTH} characters`}
        aria-describedby={showHint ? HINT_ID : undefined}
      />
      {showHint && (
        <span
          id={HINT_ID}
          role="alert"
          className="becs-filter-validation-hint"
        >
          Please enter at least {MIN_FILTER_LENGTH} characters
        </span>
      )}
      <div className="becs-filter-button-bar">
        <Button
          type="submit"
          styleType="high-visibility"
          disabled={!isValid}
        >
          Filter
        </Button>
        <Button onClick={clearFilter}>Clear</Button>
      </div>
    </BaseFilter>
  );
};
